import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { VideoComponent } from '../../../common/components';
import { BundleType, EntityTypeId, FieldName, ImageModel, InstaLearn, InstaLearnTopic, MediaTypeOption, NodeModel } from '../../../common/models';
import { ColorHelperService, InstaLearnApiService } from '../../../common/services';
import { Observable } from 'rxjs';



@Component({
    selector: 'lib-insta-learn-card',
    templateUrl: './insta-learn-card.component.html',
    styleUrls: ['./insta-learn-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InstaLearnCardComponent {
  @ViewChild(VideoComponent) public videoPlayer: VideoComponent;
  @ViewChild('audioElement') public audio: ElementRef<HTMLAudioElement>;

  @Input() public set item(item: InstaLearn) {
    this._item = item;

    this.isButtonDark = this.colorHelperService.isColorDark(item?.buttonColor);

    if (this.videoPlayer?.plyr) {
      this.videoPlayer.plyr.currentTime = 0;
    }
  }

  public get item() {
    return this._item;
  }

  @Input() public topic: InstaLearnTopic;
  @Input() public showLike = false;
  @Input() public previewImage?: ImageModel;
  @Input() public mediaEnabled: boolean;

  @Input() public set mediaAutoplay(value: boolean) {
    this._mediaAutoplay = value;

    this.syncMediaPlayback(value);
  }

  public get mediaAutoplay() {
    return this._mediaAutoplay;
  }

  @Input() public muted = true;

  @Input() public theme = '';

  @Output() public navigate: EventEmitter<InstaLearn> = new EventEmitter();
  @Output() public mutedChanged: EventEmitter<boolean> = new EventEmitter();

  public get mediaPlaying() {
    const videoPlaying = this.videoPlayer?.plyr?.playing;
    const audioPlaying = this.audio?.nativeElement && !this.audio.nativeElement.paused;
  
    return !!videoPlaying || !!audioPlaying;
  }

  public isButtonDark = false;

  public readonly mediaTypes: typeof MediaTypeOption = MediaTypeOption;
  public readonly fieldNames: typeof FieldName = FieldName;
  public readonly bundles: typeof BundleType = BundleType;
  public readonly entityTypeIds: typeof EntityTypeId = EntityTypeId;


  private _mediaAutoplay: boolean;

  private _item: InstaLearn;

  constructor(
    private readonly colorHelperService: ColorHelperService,
    private readonly instaLearnApiService: InstaLearnApiService
  ) { }

  public navigateToContent(event: Event): void {
    event.stopPropagation();
    this.navigate.emit(this.item);
  }

  public playMedia(event: Event = null): void {
    event?.stopPropagation();

    if (this.videoPlayer?.plyr) {
      this.playVideo();
      return;
    }

    this.playAudio();
  }

  public playVideo(): void {
    this.videoPlayer?.plyr?.playing ? this.videoPlayer?.pauseVideo() : this.videoPlayer?.startVideo();
  }

  public playAudio(event: Event = null): void {
    this.audio.nativeElement.paused ? this.audio.nativeElement.play() : this.audio.nativeElement.pause();
  }

  public toggleMute(): void {
    this.muted = !this.muted;

    this.mutedChanged.emit(this.muted);
  }

  public toggleLike(): void {
    this.item.userLiked = !this.item.userLiked;
    let request$: Observable<void>;

    if (this.item.userLiked) {
      request$ = this.instaLearnApiService.likeInstaLearn(this.item?.id);
      this.item.totalLikes++;
    } else {
      request$ = this.instaLearnApiService.removeLikeInstaLearn(this.item?.id);
      this.item.totalLikes--;
    }

    request$.subscribe();
  }

  private syncMediaPlayback(autoplay: boolean): void {
    this.audio?.nativeElement?.pause();
    this.videoPlayer?.pauseVideo();

    // Manually autoplay audio
    if (autoplay && this.audio?.nativeElement) {
      this.audio.nativeElement.currentTime = 0;
      this.audio.nativeElement.play();
    }
  }
}
